import { Component, h, Prop, Watch, Event, Element, Host} from '@stencil/core';
import { DateTime, Info } from 'luxon'
import { isEqual } from 'lodash-es';
import { KeyCode } from './key-codes';

import "@wdpr/input";

const localesWithLiteralOutputMonth = ["zh", "zh-hk", "zh-cn"];

/* eslint-disable @stencil-community/required-jsdoc */
/* eslint-disable @stencil-community/strict-boolean-conditions */

@Component({
  tag: 'wdpr-datepicker',
  styleUrl: 'wdpr-datepicker.scss',
  shadow: true,
})

export class WdprDatepicker {

  /**
   * Reference to host node
   */
  @Element() el: HTMLWdprDatepickerElement;
   /**
       * datepicker id
       *  {string}
       */
    @Prop({mutable:true}) datepickerId: string;

    /**
     * value is the selected date
     *  {string}
     */
    @Prop({mutable:true}) value: string=null;

    /**
     * Event emitted on value change
     */
    @Event({eventName:"value-changed"}) notifyValue;
    @Watch("value")
    /**
     * it create an instance of moment with date picker's value and
     * execute _updateDateInfo with it
     * param {string} newValue
     */
    _valueObserver(newValue, _oldValue) {
      var dateTime = newValue ? this._dateTime(newValue, this.format) : DateTime.now();
      this._updateDateInfo(dateTime);
  
      if (this.isEditableInputVariant()) {
  
        // First value
        if (this.firstRender) {
          // If the initial value is not set from HTML attribute, do not pre-fill the input-box
          if (!newValue && !this.showToday) {
            this._clearInputBox();
          } else {
            this._fillInputBox(dateTime);
          }
          this.firstRender = false;
          return;
        }
  
        // New incoming values after the initial value is set
        if (!newValue) {
          if (!this.invalid) {// To prevent clearing and re-setting the input value while the datepicker is in invalid state (e.g. user is typing)
            this._clearInputBox();
          }
        } else {
          this._fillInputBox(dateTime);
        }
      } else {
        this.value = dateTime.toFormat(this.format);
        this.valueLabel = dateTime.toFormat(this.formatLabel);
      }
      this.notifyValue.emit(newValue);
    }

    /**
     * valueLabel is the selected date formatted
     *  {string}
     */
    @Prop({mutable:true}) valueLabel: string;
    
    /**
     * locale is the selected locale configuration
     *  {string}
     */
    @Prop() locale: string="en"

    /**
     * format is the structure in which should be returned
     * the value
     *  {string}
     */
    @Prop() format: string="MM/dd/yyyy"
    
    /**
     * formatLabel is the structure in which should be returned
     * the valueLabel
     *  {string}
     */
     @Prop() formatLabel: string='DDDD'
     
    /**
     * editableFormatLabel is the format in which the displayed
     * part of the editable input will work
     *  {string}
     */
    @Prop() editableFormatLabel: string;
    
    /**
     * pastDays allows selecting dates before current date,
     * by default it's disabled
     *  {boolean}
     */
    @Prop() pastDays: boolean=false;
    @Watch("pastDays")
    /**
     * it update slots properties to enable/disable the selection
     * of past days
     * param {boolean} newValue
     * param {boolean} oldValue
     */
     _pastDaysObserver(oldValue) {
      if (oldValue !== undefined) {
        this._enablePreviousMonth(this._firstOfMonth);
        this._enableNextMonth(this._firstOfMonth);
        this._buildSlotsInformation(this._firstOfMonth);
      }
    }
    private getColor = (slot, index) => {
      return {
      "background-color": this._arrayItem(slot.indicators, index, "color")
    }}
  
    /**
     * it contains the information for each slot of calendar
     *  {Array}
     */
    @Prop({mutable:true}) slots: Array<any>;
    /**
     * monthTitle is the header's title of each month view,
     * format is `[MonthName] [Year]`
     *  {string}
     */
    @Prop({mutable:true}) monthTitle: string;

    /**
     * Title of month
     */
    @Prop({mutable:true}) datepickerMonthTitle: string;

    /**
     * Title of year
     */
    @Prop({mutable:true}) datepickerYearTitle: string;

    /**
     * daysOfWeek is a static array with first letter of
     * each day of week
     *  {Array}
     */
    private daysOfWeek: Array<String> =[];
    
    /**
     * enablePreviousMonth is used to enable/disable
     * previous month selection
     *  {boolean}
     */
    @Prop({mutable: true}) enablePreviousMonth: boolean;
    
    /**
     * enableNextMonth is used to enable/disable
     * next month selection
     *  {boolean}
     */
     @Prop({mutable: true}) enableNextMonth: boolean=true;
    
    /**
     * _firstOfMonth is a moment object used to build days
     * array
     *  {Object}
     */
    @Prop({mutable:true}) _firstOfMonth: any;
    /**
     * _yearMonth is the year and the month viewed in date
     * picker used to set a new value on date selection
     *  {string}
     */
    @Prop({mutable:true}) _yearMonth: string;
    /**
     * _today is static integer used to identify selectable
     * days of month
     *  {number}
     */
    private _today: string= DateTime.now().toFormat('yyyyMMdd');
    /**
     * it shows the day's name beside date input if it's true
     *  {Boolean}
     */
    @Prop() showDayName: boolean=false;
    
    /**
     * If datepicker is hidden
     */
    @Prop({reflect:true, mutable:true}) datePickerHidden: boolean=true; 
    
    /**
     * value displayed in the editable-input variant's input field
     *  {String}
     */
    @Prop({mutable:true}) textInputValue: string='';
    @Watch("textInputValue")
    _textInputValueObserver(newValue) {
      if (this.isEditableInputVariant()) {
        // if the input field is cleared by the user, set selected date to null
        if (newValue === '') {
          this.value = null;
          this._dispatchEventDateSelected(this.value);
          this.invalid = false;
          this._setInputClass();
          this._hide();
          return;
        }
  
        // Do not validate if the date is not complete
        if (this.validateAfterComplete) {
          if ((this.editableFormatLabel || this.format).length > newValue.length) {
            this.invalid = false;
            this._setInputClass();
            return;
          }
        }
  
        this._validateInput(newValue);
      }
    }
    /**
     * placeholder text displayed in the editable-input variant's input field
     *  {String}
     */
     @Prop() textInputPlaceholder: string="";

    /**
     * the label to display above the datepicker element
     *  {String}
     */
    @Prop() inputLabel: string="";
    
    /**
     * determines the variant of the datepicker
     *  {String}
     */
    @Prop() variant: string= "right-label";
    
    /**
     * determines whether or not to show today as the initially selected date
     *  {Boolean}
     */
    @Prop() showToday: boolean=false;
    
    /**
     * if true, will not allow the calendar drop-down to open after clicking on the label
     *  {Boolean}
     */
    @Prop() disableLabelClick: boolean=false;

    /**
     * {
     *  date: string,
     *  type: string,
     *  color: string
     * }
     * specifies a list of dates which should have indicators displayed beneath them
     *  {Array<Object>}
     */
    @Prop() indicators: Array<any>=[];
    @Watch("indicators")
    _formatIndicators(indicators, old) {
      if(isEqual(old, indicators))
        return;
      this._formattedIndicators = indicators.map(indicator => this._formatIndicator(indicator, this.locale));
      this._buildSlotsInformation(this._firstOfMonth);
    }

    /**
     * Indicators in readable format
     */
    @Prop() _formattedIndicators: Array<any>=[];
  
    /**
     * specifies a list of dates which should display with highlight colors
     *  {Array<String>}
     */
    @Prop() highlightedDates: Array<any>=[];
    @Watch("highlightedDates")
    _formatHighlightedDates(highlightedDates, old) {
      if(isEqual(old, highlightedDates))
        return;
      this._formattedHighlightedDates= highlightedDates.map(highlightedDate => this._formatHighlightedDate(highlightedDate, this.locale));
      this._buildSlotsInformation(this._firstOfMonth);
    }

    /**
     * Highlighted dates in readable format
     */
    @Prop() _formattedHighlightedDates: Array<any>=[];
    
    /**
     * if the text input displays an invalid date, this property will be true
     *  {Boolean}
     */
    @Prop({mutable:true, reflect:true}) invalid: boolean=false;
    /**
     * Event emitted when invalid state changes
     */
    @Event({eventName:"invalid-changed"}) invalidChanged;
    @Watch("invalid")
    _setInputClass() {
      if (this.isEditableInputVariant()) {
        const input = this.el.shadowRoot.querySelector('wdpr-input') as HTMLWdprInputElement;
        if (input) {
          input.valid = !this.invalid;
          input.value=input.value;
        }
      }
    }
    /**
     * set this property to put the element into "disabled" state
     *  {Boolean}
     */
    @Prop({reflect:true}) disabled: boolean=false;

    /**
     * specifies a list of dates which should be enabled in the calendar
     *  {Array<String>}
     */
    @Prop() enabledDates: Array<any>;
    @Watch('enabledDates')
    _formatEnabledDates(enabledDates, old) {
      if(isEqual(old, enabledDates))
        return;
      this._formattedEnabledDates = enabledDates.map(date => this._dateTime(date, this.format).toFormat('yyyyMMdd'));
      this._buildSlotsInformation(this._firstOfMonth);
    }
  
    /**
     * Enabled dates in readable format
     */
    @Prop() _formattedEnabledDates: any;
    
    /**
     * the minimum date to enable in the calendar.
     * All dates greater than and including this value will be enabled in the calendar
     *  {Array<String>}
     */
    @Prop() minEnabledDate: string;
    @Watch("minEnabledDate")
    setMinDate(minDate){
      if(!minDate)
        return;
      this._formattedMinEnabledDate = this._dateTime(minDate, this.format).toFormat('yyyyMMdd');
      this._enablePreviousMonth(this._firstOfMonth);
      this._enableNextMonth(this._firstOfMonth);
  
      this._buildSlotsInformation(this._firstOfMonth);
    }

    /**
     * Min date in readable format
     */
    @Prop() _formattedMinEnabledDate: string;
    
    /**
     * the maximum date to enable in the calendar.
     * All dates less than and including this value will be enabled in the calendar
     *  {Array<String>}
     */
    @Prop() maxEnabledDate: string;
    @Watch("maxEnabledDate")
    _setMaxDate(maxDate) {
      if(!maxDate)
        return;
      this._formattedMaxEnabledDate = this._dateTime(maxDate, this.format).toFormat('yyyyMMdd');
      this._enablePreviousMonth(this._firstOfMonth);
      this._enableNextMonth(this._firstOfMonth);
  
      this._buildSlotsInformation(this._firstOfMonth);
    }

    /**
     * If this is set to true, the input text will only be validated after
     * it reaches the length of the label format provided.
     *  {Boolean}
     */
    @Prop() validateAfterComplete: boolean=false;
    
    /**
     * Max enabled date in component format
     */
    @Prop() _formattedMaxEnabledDate: string;
    
    /**
     * `required` Determines if date is required to fill (only for editable input)
     */
    @Prop() required: boolean=false;
  
    /**
     * `editableInputErrorMessage` Shows the error message on editable input
     */
    @Prop() editableInputErrorMessage: any;
    
    /**
     * `firstRender` is a flag to determine if the very first value is about to be set
     */
    @Prop() firstRender: boolean=true;
    
    /**
     * when true, the error message will be a plain text instead of a wdpr-messages instance
     */
    @Prop() usePlainTextErrorMessage: boolean=false;
    
    /**
     * when true, the calendar will not close when the user clicks outside of the datepicker element
     */
    @Prop() disableCloseOnClick: boolean=false;
    
    /**
     * when true, the navigation buttons in the calendar header will be hidden
     */
    @Prop() hideNavigation: boolean=false;

    componentWillLoad() {
      this.datepickerId = this._generateRandomId();
      this._valueObserver(this.value, null);

      this._formatIndicators(this.indicators, undefined);
      this._formatEnabledDates(this.enabledDates, undefined);
      this._formatHighlightedDates(this.highlightedDates, undefined);
        this._setMaxDate(this.maxEnabledDate)
        this.setMinDate(this.minEnabledDate)
      if (this.isAlwaysOpenVariant()) {
        this.datePickerHidden=(false);
      }
      this._getDaysOfWeek();
      // To prevent clicks on the month name, arrows or disabled dates from closing the calendae
  
      this.clickOutsideListener = _event => {
        this._hide(); // This function call will also remove the listener once the calendar is hidden
      }
      // To close the calendar if the user clicks anywhere outside it
    }

    /**
     * Listener to close on click outside of component
     */
    @Prop() clickOutsideListener;
    
  
   private  _addClickOutsideCalendarListener() {
      // Manually trigger a click on window to close other open datepickers on the same page (if any)
      var event = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
      window.dispatchEvent(event);
  
      if (!this.disableCloseOnClick) {
        window.addEventListener('click', this.clickOutsideListener);
      }
    }
  
    private _removeClickOutsideCalendarListener() {
      if (!this.disableCloseOnClick) {
        window.removeEventListener('click', this.clickOutsideListener);
      }
    }
  
    /**
     * get moment local config with locale
     */
     private _dateTime(value, format) {
      return DateTime.fromFormat(value, format, {locale:this.locale});
    }
  
    private _getDaysOfWeek() {
      let weekDays=Info.weekdays("narrow", {locale:this.locale});
        // This is currently done to go from ISO standard weeks, which start on Monday, to en-US weekdays, which start on Sun.
        // The international API is working to support firstDayOfWeek based on locale-- once that is live, this will be updated
      this.daysOfWeek=weekDays.slice(0,6);
      this.daysOfWeek.unshift(weekDays[6]);
    }
  
    /**
     * it updates date properties and execute the method which will
     * create information of calendar's slots
     * param {Object} firstOfMonth
     */
     private _updateDateInfo(firstOfMonth) {
      let currentMonthTitle = "";
      this._firstOfMonth = DateTime.fromObject({month: firstOfMonth.month, year:firstOfMonth.year}, {locale:this.locale});
      if (this.locale.toLowerCase() === "ja") {
        currentMonthTitle = new Date(firstOfMonth.year, firstOfMonth.month - 1).toLocaleDateString('ja', { month: 'short' });
      } else {
        currentMonthTitle = firstOfMonth.toFormat(
          localesWithLiteralOutputMonth.includes(this.locale.toLowerCase()) ? "LLLL" : "MMMM"
        );
      }
      this.monthTitle = currentMonthTitle;
      this.datepickerMonthTitle = currentMonthTitle;
      this.datepickerYearTitle = firstOfMonth.toFormat('yyyy');
      this._yearMonth = firstOfMonth.toFormat('yyyyMM');
      this._enablePreviousMonth(firstOfMonth);
      this._enableNextMonth(firstOfMonth);
      this._buildSlotsInformation(firstOfMonth);
    }
  
    /**
     *  it updates enablePreviousMonth property to allow selecting
     * previous month
     *  {Object} firstOfMonth
     */
     private _enablePreviousMonth(firstOfMonth) {
      // If there is no minimum date set, always enable the previous month button.
      if (this._formattedMaxEnabledDate && !this._formattedMinEnabledDate) {
        this.enablePreviousMonth=(true);
        return;
      }
  
      // If there is a minimum date set, determine if the current month contains the minimum.
      if (this._formattedMinEnabledDate) {
        const difference = this._dateTime(this._formattedMinEnabledDate, 'yyyyMMdd').diff(firstOfMonth);
        this.enablePreviousMonth=(difference <= 0);
        return;
      }
      
      const firstOfPreviousMonth = firstOfMonth.plus({month: -1, day:DateTime.now().day});
      const difference = DateTime.now() - firstOfPreviousMonth;
      this.enablePreviousMonth=(this.pastDays || difference < 0);
    }
  
    /**
     * updates the enableNextMonth property to allow selecting the next month.
     * The next month is enabled unless there is a maximum date provided.
     * param {Object} firstOfMonth
     */
     private _enableNextMonth(firstOfMonth) {
      if (!this._formattedMaxEnabledDate) {
        this.enableNextMonth=true;
        return true;
      }
  
      const difference = this._dateTime(this._formattedMaxEnabledDate, 'yyyyMMdd').diff(firstOfMonth);
      this.enableNextMonth=(difference > 0);
    }
  
    /**
     * it creates an array of object with the information for each
     * slot of calendar
     * param {Object} firstOfMonth
     */
     private _buildSlotsInformation(first) {
      let firstOfMonth= DateTime.fromObject({month: first.month, year:first.year}, {locale:this.locale})
      const slots = [];
      const lastOfMonth = firstOfMonth.plus({month: 1}).minus({day:1});
      const selectedDay = this.value ? this._dateTime(this.value, this.format).toFormat('yyyyMMdd') : null;

      let weekday = firstOfMonth.weekday == 7 ? 1 : firstOfMonth.weekday +1
      // if `1 - firstOfMonth.weekday` returns a number less than 1 it means that there are days of the previous month in the first week
      for (let dayOfMonth = 2 - weekday; lastOfMonth.day >= dayOfMonth; dayOfMonth++) {
        slots.push(this._buildSlotAttributes(dayOfMonth, selectedDay));
      }
      this.slots=(slots);
    }
  
    /**
     * if dayOfMonth is valid it will return an object with slot's
     * attributes
     * param {string} dayOfMonth
     * param {string} selectedDay
     * @return {Object} slot's attributes
     */
     private  _buildSlotAttributes(dayOfMonth, selectedDay) {
      if (dayOfMonth > 0) {
        const date = `${this._yearMonth}${dayOfMonth < 10 ? '0' : ''}${dayOfMonth}`;
        const highlightedDate = this._formattedHighlightedDates.filter(highlightedDate => highlightedDate.date === date)[0]
        const slot = {
          text: dayOfMonth,
          tabindex: this._isDateEnabled(date) ? '0' : '',
          class: this._getDayClass(date, selectedDay, highlightedDate),
          inlineStyle: this._getDayInlineStyle(highlightedDate),
          selected: (date === selectedDay),
          available: this._isDateEnabled(date),
          indicators: this._formattedIndicators.filter(indicator => indicator.date === date)
        };
        return slot;
      }
      return { tabindex: '-1', class: '' };
    }
  
    private _arrayItem(change, _index, _path) {
      let item=change[_index];
      if(_path == "type")
        return(item.type)
      if(_path == "color")
        return(item.color)
      return("");
    }
  
    private _hasSingleIndicator(slot) {
      if (slot.indicators) {
        return slot.indicators.length === 1;
      }
    }
  
    /**
     * Method to see if slot has first indicator
     * @param slot 
     * @returns true or false
     */
    @Prop() _hasFirstIndicator = (slot) => {
      if (slot.indicators && slot.indicators.length > 1) {
        return slot.indicators ? slot.indicators[0] : false;
      }
    }
  
    /**
     * Method to see if slot has second indicator
     * @param slot 
     * @returns true or false
     */
    @Prop() _hasSecondIndicator = (slot) => {
      if (slot.indicators && slot.indicators.length > 1) {
        return slot.indicators ? slot.indicators[1] : false;
      }
    }
  
    /**
     * determines if the provided date should be enabled in the calendar dropdown
     * param {string} date
     * @return {boolean} returns true if the date should be enabled, false if disabled
     */
     @Prop() _isDateEnabled = (date)  => {
      if (this.isAlwaysOpenVariant() && this.disabled) {
        return false;
      }
  
      if (this._formattedMaxEnabledDate || this._formattedMinEnabledDate) {
        let enabled = true;
        if (this._formattedMaxEnabledDate && date > this._formattedMaxEnabledDate) {
          enabled = false;
        }
        if (this._formattedMinEnabledDate && date < this._formattedMinEnabledDate) {
          enabled = false;
        }
  
        return enabled;
      }
      return this._formattedEnabledDates ?
        this._formattedEnabledDates.indexOf(date) > -1 :
        (date >= this._today || this.pastDays);
    }
  
    /**
     * it returns the css classes for each day
     * param {string} date
     * param {string} selectedDay
     * @return {string} classes
     */
     @Prop() _getDayClass = (date, selectedDay, highlighted) => {
      let classes = '';
      if (this.isAlwaysOpenVariant() && this.disabled) {
        classes += 'disabled';
      } else {
        classes += date === selectedDay ? 'active light' : '';
        classes += date === this._today ? ' today' : '';
        classes += !this._isDateEnabled(date) ? ' disabled' : '';
        classes += highlighted ? ' highlighted' : '';
      }
      return classes;
    }
  
    private _getDayInlineStyle(highlightedDate) {
      if(!highlightedDate)
        return;
      let color = highlightedDate.backgroundColor ? {"background-color": highlightedDate.backgroundColor} : "";
      let textColor = highlightedDate.textColor ? {"color": highlightedDate.textColor} : "";
      return {
        color,
        textColor
      }
    }
    /**
     * Method to validate input
     * @param value 
     */
    @Prop() _validateInput= (value) => {
      if (value) {
        var strictLocaleObject = DateTime.fromFormat(value, this.format, {locale: this.locale})
        if (!strictLocaleObject.invalid && this._isDateEnabled(strictLocaleObject.toFormat('yyyyMMdd'))) {
          var validFormattedDate = (strictLocaleObject.toFormat(this.format));
          const newDate = this._dateTime(validFormattedDate, this.format);
          this.invalid = false;
          this.value = newDate.toFormat(this.format);
          this.valueLabel = newDate.toFormat(this.editableFormatLabel || this.formatLabel);
          this._dispatchEventDateSelected(this.value);
        } else { // An invalid date string is entered in the input field
          this.invalid = true;
          this.value = null;
          this._dispatchEventDateSelected(null);
          this._setInputClass();
        }
      }
    }
  
    /**
     * it executes the method '_updateDateInfo' if 'enablePreviousMonth'
     * is true
     * param {Object.<Event>} event
     */
    private nextMonthKeydownHandler = (event) => {
      if ([KeyCode.Enter, KeyCode.Escape].includes(event.code)) {
        this._executeIfValidKeyPressed(event, () => {
          this._updateDateInfo(this._firstOfMonth.plus({ month: 1 }));
        });        
      }
    }
  
    /**
     * Event emitted when next month is successfully clicked
     */
    @Event({eventName:"next-month-click"}) nextMonthClicked;
    /**
     * it executes the method '_updateDateInfo'
     * param {Object.<Event>} event
     */
     private nextMonthClickHandler = () => {
      if (this.enableNextMonth) {
        this._updateDateInfo(this._firstOfMonth.plus({month: 1}));
        this.nextMonthClicked.emit({selectedMonth:this._firstOfMonth.toFormat('yyyyMM')})
      }
    }
  
    /**
     * it executes the method '_updateDateInfo' if 'enablePreviousMonth'
     * is true and the key pressed is enter or spacebar
     * param {Object.<Event>} event
     */
     private prevMonthKeydownHandler = (event) => {
      if ([KeyCode.Enter, KeyCode.Escape].includes(event.code)) {
        this._executeIfValidKeyPressed(event, () => {
          if (this.enablePreviousMonth) {
            this._updateDateInfo(this._firstOfMonth.plus({ month: -1 }));
          }
        });
      }
    }

    /**
     * Event emitted when previous month is successfully clicked
     */
    @Event({eventName:"prev-month-click"}) prevMonthClicked;
  
    /**
     * it executes the method '_updateDateInfo' if 'enablePreviousMonth'
     * is true
     * param {Object.<Event>} event
     */
     private prevMonthClickHandler = () => {
      if (this.enablePreviousMonth) {
        this._updateDateInfo(this._firstOfMonth.minus({month: 1}));
        this.prevMonthClicked.emit({
            selectedMonth: this._firstOfMonth.toFormat('yyyyMM')
          });
      }
    }
  
    /**
     * it executes the method '_updateSelectedDay' if key pessed is valid
     * param {Object.<Event>} event
     */
     private selectDayKeydownHandler = (event) => {
      this._executeIfValidKeyPressed(event, this._updateSelectedDay.bind(event));
    }
  
    /**
     * it executes the method '_updateSelectedDay' if key pessed is valid
     * param {Object.<Event>} event
     */
     private setAriaLabel(element) {
      let newDate = this._dateTime(this._yearMonth + element.text, 'yyyyMMdd').toFormat(this.formatLabel);
      if (element && element.selected && newDate) {
        newDate += ', Selected date';
      }
      return newDate;
    }
  
    /**
     * it executes the method '_updateSelectedDay'
     * param {Object.<Event>} event
     */
     private selectDayClickHandler = (event) => {
      this._updateSelectedDay(event);
    }
  
    /**
     * it checks if key pressed is enter or spacebar
     * param {Object.<Event>} event
     * param {number} keyCode
     * param {Object}
     * param {Function} callback
     */
     private _executeIfValidKeyPressed(event, callback) {
      switch (event.code) {
        case KeyCode.Enter:
          event.preventDefault();
          callback(event);
          break;
        case KeyCode.ArrowRight:
          event.preventDefault();
          this._rightArrowKey(event);
          break;
        case KeyCode.Tab:
          var currentDay = Number(event.target.getAttribute('data-day'));
          var lastSlot = this.slots[this.slots.length - 1];
          if (lastSlot.text === currentDay) {
            if (event.shiftKey) {
              this._leftArrowKey(event);
            } else {
              this._rightArrowKey(event);
            }
          }
          break;
        case KeyCode.ArrowLeft:
          event.preventDefault();
          this._leftArrowKey(event);
          break;
        case KeyCode.ArrowUp:
          event.preventDefault();
          this._upArrowKey(event);
          break;
        case KeyCode.ArrowDown:
          event.preventDefault();
          this._downArrowKey(event);
          break;
        case KeyCode.Escape:
          event.preventDefault();
          this._hide()
          break;
        default:
          break;
      }
    }
  
    /**
     * verify if the next number (up, down, right, left) is
     * available in the current month
     */
     private _validateNumberElement(numberDay: number) {
      var elementSelector = '.day-number[data-day="' + numberDay + '"]';
      var element = this.el.shadowRoot.querySelector('#calendar-container').querySelector(elementSelector);
      return element;
    }

    private renderPromiseResolve;

    /*
      Returns a promise once in-progress render is complete, used so that
      DOM selection after calendar month changes queries after the new
      month has rendered.
    */
    private waitForNextRender() {
      return new Promise(resolve => this.renderPromiseResolve = resolve);
    }
    
    componentDidRender() {
      // Update once render completes
      if (this.renderPromiseResolve) {
        this.renderPromiseResolve();
      }
    }
  
    private  _rightArrowKey = async (event) =>{
      event.preventDefault();
      var numberDay = Number(event.target.getAttribute('data-day'));
  
      if (numberDay > 0) {
        var element = this._validateNumberElement(numberDay + 1) as HTMLElement;
        if (element) {
          // next day is available, so we focus on it
          element.focus();
        } else if (this.enableNextMonth) {
          // we are in the last day of month, we need to
          // change to next month and select first day
          this.nextMonthClickHandler();
          var _this = this;
          // Wait for next render so it gets the first day of next
          // month instead of current
          await _this.waitForNextRender();
            var firstDayElement = _this._validateNumberElement(1) as HTMLElement;
            firstDayElement.focus();
        }
      }
    }
  
    private _leftArrowKey = async (event) => {
      event.preventDefault();
      var numberDay = Number(event.target.getAttribute('data-day'));
  
      if (numberDay > 0) {
        var element = this._validateNumberElement(numberDay - 1) as HTMLElement;
        if (element && element.tabIndex !== -1) {
          // previous day is available, so we focus on it
          element.focus();
        } else if (this.enablePreviousMonth) {
          // we are in the first day of month, we need to
          // change to previous month and select last day
          this.prevMonthClickHandler();
          var _this = this;
          // Wait for next render so it gets the last day of previous
          // month instead of current
          await _this.waitForNextRender();
            var lastSlot = _this.slots[_this.slots.length - 1];
            var lastDayElement = _this._validateNumberElement(lastSlot.text) as HTMLElement;
            lastDayElement.focus();
        }
      }
    }
  
    private _upArrowKey = async (event) => {
      var numberDay = Number(event.target.getAttribute('data-day'));
       var workingDate = DateTime.fromObject({day: numberDay, month: this._firstOfMonth.month, year: this._firstOfMonth.year}, {locale:this.locale}).minus({days: 7});
   
       var initialMonth = this._firstOfMonth.month;
       var nextMonth = workingDate.month;
 
       if (initialMonth === nextMonth) {
         // we are in the same month
         var previousWeekElement = this._validateNumberElement(workingDate.day) as HTMLElement;
         if (previousWeekElement) {
           previousWeekElement.focus();
         }
       } else {
         // month needs to be changed
         this.prevMonthClickHandler();
         var _this = this;
         // Wait for next render so it gets the last week of last
          // month instead of current
         await _this.waitForNextRender();
         
           var previousWeekElement = _this._validateNumberElement(workingDate.day) as HTMLElement;
           if (previousWeekElement) {
             previousWeekElement.focus();
           }
         }
     }
  
    private _downArrowKey = async (event) => {
      var numberDay = Number(event.target.getAttribute('data-day'));
      var workingDate = DateTime.fromObject({day: numberDay, month: this._firstOfMonth.month, year: this._firstOfMonth.year}, {locale:this.locale}).plus({days: 7});
  
      var initialMonth = this._firstOfMonth.month;
      var nextMonth = workingDate.month;
  
      if (initialMonth === nextMonth) {
        // we are in the same month
        var nextWeekElement = this._validateNumberElement(workingDate.day) as HTMLElement;
        if (nextWeekElement) {
          nextWeekElement.focus();
        }
      } else {
        // month needs to be changed
        this.nextMonthClickHandler();
        var _this = this;
        await _this.waitForNextRender();
        // Wait for next render so it gets the first week of next
        // month instead of current
          var nextWeekElement = _this._validateNumberElement(workingDate.day) as HTMLElement;
          if (nextWeekElement) {
            nextWeekElement.focus();
          }
      }
    }
  
    /**
     * it checks if new date is valid and it update date picker's value
     * param {Object} event
     * param {Object} event.originalTarget
     * param {Object} event.toElement
     * param {Object} event.srcElement
     */
     private _updateSelectedDay = ({ originalTarget, toElement, srcElement }) => {
      let element = originalTarget || toElement || srcElement;

      if(!element)
        return;
  
      while (element.parentNode && element.className.indexOf('day-number') === -1) {
        element = element.parentNode;
      }
  
      if (element.getAttribute('data-available') !== null) {
        const newDate = DateTime.fromObject({day: element.getAttribute('data-day'), month: this._firstOfMonth.month, year: this._firstOfMonth.year}, {locale:this.locale}, 'yyyyMMdd');
        this.value = newDate.toFormat(this.format);
        this.valueLabel = newDate.toFormat(this.formatLabel);
        if (element.selected) {
          element.setAttribute('aria-label', this.setAriaLabel(element));
        }
        this._dispatchEventDateSelected(this.value);
        this._hide();
      }
    }
  
    private _fillInputBox(dateTime) {
      this.textInputValue = dateTime.toFormat(this.editableFormatLabel || this.format);
    }
  
    private _clearInputBox() {
      this.textInputValue = '';
    }
  
    /**
     * Event emitted when selected date changes
     */
    @Event({eventName:"selected-date"}) selectedDate;
    /**
     * it launches an event notifying that a day was selected
     * param {string} selectedDate
     */
     private _dispatchEventDateSelected(selectedDate) {
      this.selectedDate.emit({selectedDate: selectedDate})
    }
  
    /**
     * Event emitted when open state changes
     */
    @Event({eventName:"open-state"}) openState;
    /**
     * it launches an event notifying that datepicker open/close
     * param {string} isOpen
     */
     private _dispatchEventDatepickerOpen(newOpened) {
      this.openState.emit({isOpen: newOpened})
    }
  
    /**
     * show date picker
     */
     @Prop() showHandler = (event) => {
      event.stopPropagation();
  
      if (this.datePickerHidden) {
        this.datePickerHidden=false;
      }
    }
  
    /**
     * Toggle datepicker
     */
    @Prop() toggleHandler = (event) => {
      if (this.disabled) {
        return;
      }
  
      if (this.disableLabelClick) {
        const path = event.path || (event.composedPath && event.composedPath()) || [];
        const label = path.filter(node => node.id && node.id.toLowerCase() === 'datepickerlabel')[0];
        if (label) return;
      }
  
      event.stopPropagation();
  
      if (this.datePickerHidden) {
        this._reveal();
      } else {
        this._hide();
      }
    }
  
    /**
     * Open calendar
     * @param event 
     * @returns 
     */
    @Prop() openCalendar = (event) => {
      event.stopPropagation();
  
      const path = event.path || (event.composedPath && event.composedPath()) || [];
      const button = path.filter(node => node.tagName && node.tagName.toLowerCase() === 'i')[0];
  
      // if we are pressing a button, let that be handled separately
      if (button) {
        return;
      }
  
      if (this.datePickerHidden) {
        this._reveal();
      }
    }
  
    /**
     * Close calendar
     * @param _event 
     * @returns 
     */
    @Prop() closeCalendar = (_event) => {
      if (this.disabled) {
        return;
      }
  
      if (!this.datePickerHidden) {
        this._hide();
      }
    }
  
    /**
     * handles input validation when user focus out from editable input datepicker
     */
     private editableInputFocusOutHandler = (event) => {
      if (this.isEditableInputVariant()) {
        const target = event.relatedTarget || document.activeElement;
        const datepickerId = target.getAttribute('datepicker-id');
  
        if (datepickerId !== this.datepickerId) {
          this._validateInput(this.textInputValue);
          this._setInputClass();
        }
      }
    }
  
    private _hide() {
      if (!this.isAlwaysOpenVariant()) {
        this._dispatchEventDatepickerOpen(false);
        this.datePickerHidden=(true);
        this._removeClickOutsideCalendarListener();
      }
    }
  
    private _reveal() {
      // Setup the calendar slots to display the month of the selected date.
      // If there is no selected date, display the month of the current date.
      var dateTime = this.value ? this._dateTime(this.value, this.format) : DateTime.now();
      this._updateDateInfo(DateTime.fromObject({year:dateTime.year, month: dateTime.month, day:1}, {locale:this.locale}));
  
      this._dispatchEventDatepickerOpen(true);
      this.datePickerHidden=(false);
      this._addClickOutsideCalendarListener();
    }
  
    /**
     * Checks if previous month can be selected
     * param {boolean} enablePreviousMonth
     * @return {string} tabindex
     */
     private getPreviousMonthClass(enablePreviousMonth) {
      return !enablePreviousMonth ? 'disabled' : '';
    }
  
    /**
     * Checks if next month can be selected
     * param {boolean} enableNextMonth
     * @return {string} tabindex
     */
     private getNextMonthClass(enableNextMonth) {
      return !enableNextMonth ? 'disabled' : '';
    }

    /**
     * Method to get weekday name of a given date with format
     * @param date 
     * @param format 
     * @returns 
     */
    @Prop() getDayName = (date, format) => {
      const dateTime = this._dateTime(date, format);
      return !dateTime.invalid ? dateTime.weekdayShort : '';
    }
  
    private getLabelClass() {
      return this.disableLabelClick ? '' : 'link';
    }
  
    private isRightLabelVariant() {
      return this.variant === 'label' || this.variant === 'right-label';
    }
  
    private isLeftLabelVariant() {
      return this.variant === 'left-label';
    }
  
    private isInputVariant() {
      return this.variant === 'input';
    }
  
    private isEditableInputVariant() {
      return this.variant === 'editable-input';
    }
  
    private isAlwaysOpenVariant() {
      return this.variant === 'always-open';
    }

    /**
     * Method to format indicator based on locale
     * @param indicator 
     * @param _locale 
     * @returns 
     */
    @Prop() _formatIndicator = (indicator, _locale) => {
      // Backwards compatibility for indicators as string arrays
      if (typeof indicator === 'object') {
        return {
          date: DateTime.fromFormat(indicator.date, this.format, {locale:this.locale}).toFormat('yyyyMMdd'),
          color: indicator.color,
          type: indicator.type
        }
      } else {
        return {
          date: DateTime.fromFormat(indicator, this.format, {locale:this.locale}).toFormat('yyyyMMdd'),
        }
      }
    }

    componentDidLoad() {
      this.el.shadowRoot.querySelector('#calendar-container').addEventListener('click', event => {
        event.stopPropagation();
      });
    }
  
    
  
    private  _formatHighlightedDate(highlightedDate, locale) {
      // Backwards compatibility for highlighted dates as string arrays
      if (typeof highlightedDate === 'object') {
        return {
          date: DateTime.fromFormat(highlightedDate.date, this.format, {locale:locale}).toFormat('yyyyMMdd'),
          backgroundColor: highlightedDate.backgroundColor,
          textColor: highlightedDate.textColor
        }
      } else {
        return {
          date: DateTime.fromFormat(highlightedDate, this.format, {locale:locale}).toFormat('yyyyMMdd'),
        }
      }
    }
  
    /**
     * Generate a random ID for the datepicker
     * */
    @Prop() _generateRandomId = () => {
      let result = '';
      const length = 6;
      const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  
      for (let i = length; i > 0; --i) {
        result += chars[Math.floor(Math.random() * chars.length)];
      }
  
      return result;
    }
  
    private  _computeErrorMessageForWdprInput(editableInputErrorMessage, usePlainTextErrorMessage) {
      if (usePlainTextErrorMessage) {
        return;
      } else {
        return editableInputErrorMessage;
      }
    }
  
    private _showPlainTextErrorMessage(invalid, usePlainTextErrorMessage) {
      return invalid && usePlainTextErrorMessage;
    }

    /**
     * Bind value to text input
     * @param event 
     */
    @Prop() bindValue =(event) =>
    {
      this.textInputValue=event.target.value;
      event.target.value=this.textInputValue;
    }

    private iconButtonKeydownHandler = (event) => {
      const keyCode = event.code;
      if (keyCode === KeyCode.Escape) {
        event.preventDefault();
        this._hide()
      }
    }
  
    private monthNameKeydownHandler = (event) => {
      const keyCode = event.code;
      if (keyCode === KeyCode.Escape) {
        event.preventDefault();
        this._hide()
      }
    }

  render() {
    return(
      <Host>
        {this.isInputVariant() &&
        <div id="input-container" on-click={this.toggleHandler}>
            <label aria-label={this.inputLabel}>{this.inputLabel}</label>
            <span class="font16" aria-label={this.valueLabel}>{this.valueLabel}</span>


            <button id="calendar-button-right"
                    class="icon-button"
                    on-keydown={this.iconButtonKeydownHandler}
                    aria-label="Choose date">
                <i class="di di-calendar-month di18"></i>
            </button>
        </div>
        }
        {this.isEditableInputVariant() &&
          <div id="editable-input-container"
          aria-label={this.inputLabel}
          datepicker-id={this.datepickerId}
          on-focusout={this.editableInputFocusOutHandler}
          >
          <wdpr-input
              on-click={this.openCalendar}
              on-icon-button-click={this.toggleHandler}
              class="editable-input"
              type="text"
              show-label-with-placeholder
              icon-button="di-calendar-month"
              label={this.inputLabel} clearbutton={false}
              placeholder={this.textInputPlaceholder}
              value={this.textInputValue}
              on-value-changed={this.bindValue}
              disabled={this.disabled}
              icon-button-aria-label="date picker"
              icon-button-aria-expanded={!this.datePickerHidden}
              required={this.required}
              error-message={this._computeErrorMessageForWdprInput(this.editableInputErrorMessage, this.usePlainTextErrorMessage)}
              no-validate-on-change="true"
              no-validate-on-blur="true">
          </wdpr-input>
      </div>
        }
        {this._showPlainTextErrorMessage(this.invalid, this.usePlainTextErrorMessage) &&
          <div id="plain-text-error-message">{this.editableInputErrorMessage}</div>
        }
        {this.isRightLabelVariant() &&
          <div id="label-container" on-click={this.toggleHandler}>

          <button id="calendar-button-label-left"
                  class="icon-button"
                  aria-label="date picker">
              <i class="di di-calendar-month di20 primary-dark"></i>
          </button>

          <span id="datepickerLabel" class={this.getLabelClass()} aria-label={this.valueLabel}>{this.valueLabel}</span>
      </div>
        }
        {this.isLeftLabelVariant() &&
          <div id="label-container" on-click={this.toggleHandler}>

          <span id="datepickerLabel" class={this.getLabelClass()} aria-label={this.valueLabel}>{this.valueLabel}</span>

          <button id="calendar-button-label-right"
                  class="icon-button"
                  aria-label="date picker">
              <i class="di di-calendar-month di20 primary-dark"></i>
          </button>
      </div>
        }

    <div id="calendar-container"
        datepicker-id={this.datepickerId}
        on-focusout={this.editableInputFocusOutHandler}>
        <div class="calendar-header">

            <button datepicker-id={this.datepickerId}
                    on-keydown={this.prevMonthKeydownHandler}
                    on-click={this.prevMonthClickHandler}
                    class={"icon-button arrow-button " + this.getPreviousMonthClass(this.enablePreviousMonth)}
                    aria-label="Previous month"
                    disabled={this.disabled? false: !this.enablePreviousMonth}
                    aria-disabled={this.disabled? false: !this.enablePreviousMonth}>
                <i hidden={this.hideNavigation} class="di di-previous-2 di20"></i>
            </button>
            <span
              tabindex={this.disabled ? -1 : 0}
              on-keydown={this.monthNameKeydownHandler}
              class="month-title" aria-label={this.datepickerMonthTitle + " " + this.datepickerYearTitle}>
                <span class="month">{this.datepickerMonthTitle}</span>
                &nbsp;
                <span class="year">{this.datepickerYearTitle}</span>
            </span>
            <button datepicker-id={this.datepickerId}
                    on-keydown={this.nextMonthKeydownHandler}
                    on-click={this.nextMonthClickHandler}
                    class={"icon-button arrow-button " + this.getNextMonthClass(this.enableNextMonth)}
                    aria-label="Next month"
                    disabled={this.disabled? false: !this.enableNextMonth}
                    aria-disabled={this.disabled? false: !this.enableNextMonth}>
                <i hidden={this.hideNavigation} class="di di-next-2 di20"></i>
            </button>
        </div>

        <div class="separator"></div>
        <div class="calendar-body">
            <div class="days-of-week">
              
              {this.daysOfWeek.map((dayOfWeek) =>
                <div class="day-label">{dayOfWeek}</div>
            )}
            </div>

            <div class="separator"></div>

            <div class="calendar">
            {this.slots.map((slot) =>
                <div datepicker-id={this.datepickerId}
                on-click={this.selectDayClickHandler}
                on-keydown={this.selectDayKeydownHandler}
                data-day={slot.text}
                class={"day-number " + slot.class}
                style={slot.inlineStyle}
                tabindex={slot.tabindex}
                data-available={slot.available}
                aria-label={this.setAriaLabel(slot)}>
                <div class="day-container">{slot.text}</div>

                {this._hasSingleIndicator(slot) 
                &&  <span
                style= {this.getColor(slot, 0)}
                class={"indicator single " + this._arrayItem(slot.indicators, 0, 'type')}></span>
                }

                {this._hasFirstIndicator(slot) 
                &&  <span
                style= {this.getColor(slot, 0)}
                class={"indicator single " + this._arrayItem(slot.indicators, 0, 'type')}></span>
                }
                {this._hasSecondIndicator(slot) 
                &&  <span
                style= {this.getColor(slot, 1)}
                class={"indicator single " + this._arrayItem(slot.indicators, 1, 'type')}></span>
                }
              </div>
            )}
        </div>
    </div>
    </div>
    </Host>);
  }
}
